import { ENV } from '@/shared/config';

import { createLotusAuthHeadersOptional } from './hostageChat';

export interface ShelfEntry {
  key: string;
  metadata: Record<string, unknown>;
}

export interface ShelfLoadResult {
  key: string;
  value: Array<Record<string, unknown>>;
  metadata: Record<string, unknown>;
}

export class ShelfRequestError extends Error {
  public readonly status: number;

  constructor(status: number, statusText: string) {
    super(`shelf request failed: ${status} ${statusText}`);
    this.name = 'ShelfRequestError';
    this.status = status;
  }
}

async function shelfFetch(path: string, init?: RequestInit): Promise<Response> {
  const headers = await createLotusAuthHeadersOptional();
  const res = await fetch(`${ENV.apiBaseUrl}${path}`, {
    ...init,
    headers: { ...headers, ...init?.headers },
  });
  if (!res.ok) {
    throw new ShelfRequestError(res.status, res.statusText);
  }
  return res;
}

export interface ShelfStoreOptions {
  /** Keep the request alive past page teardown. Required when storing
   *  from a ``beforeunload`` handler, where a normal fetch is aborted. */
  keepalive?: boolean;
}

export async function shelfStore(
  patientId: string,
  key: string,
  value: Array<Record<string, unknown>>,
  metadata: Record<string, unknown> = {},
  options: ShelfStoreOptions = {}
): Promise<void> {
  await shelfFetch(`/greenfield/care/${patientId}/shelf/store`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ key, value, metadata }),
    keepalive: options.keepalive,
  });
}

export async function shelfRemoveItem(
  patientId: string,
  key: string,
  itemId: string
): Promise<void> {
  await shelfFetch(
    `/greenfield/care/${patientId}/shelf/remove-item?key=${encodeURIComponent(key)}&item_id=${encodeURIComponent(itemId)}`,
    { method: 'DELETE' }
  );
}

export async function shelfLoad(patientId: string, key: string): Promise<ShelfLoadResult> {
  const res = await shelfFetch(
    `/greenfield/care/${patientId}/shelf/load?key=${encodeURIComponent(key)}`
  );
  return res.json() as Promise<ShelfLoadResult>;
}

export async function shelfList(patientId: string): Promise<ShelfEntry[]> {
  const res = await shelfFetch(`/greenfield/care/${patientId}/shelf/list`);
  const body = (await res.json()) as { entries: ShelfEntry[] };
  return body.entries;
}
